import { c as _c } from "react/compiler-runtime";
import { FaroTableColumnVisibilityMenuIcon } from "@components/common/faro-table/faro-table-column-visibility-menu-icon";
import { faroTableComparator } from "@components/common/faro-table/faro-table-utils";
import { BasicInfoMemberColumn } from "@components/table/members/members-column/basic-info-member-column";
import { RoleColumn } from "@components/table/members/members-column/role-column/role-column";
import { SeatsColumn } from "@components/table/members/members-column/seats-column";
import { StatusColumn } from "@components/table/members/members-column/status-column/status-column";
import { MemberHeaders } from "@components/table/members/members-table-utils";
import { TableHeaderWithDraggableIcon } from "@components/table/table-header-with-draggable-icon";
import { UserAvatarWithMenu } from "@components/user-more-info-menu/user-avatar-with-menu";
import { useColumnPreferencesContext } from "@context-providers/table/column-preferences-provider";
import { Box, Stack } from "@mui-wrapper";
import { isGroupMember } from "@custom-types/member-type-guards";
import { useDateTime } from "@hooks/use-date-time";
import { gridStringOrNumberComparator } from "@mui/x-data-grid-pro";
import { FaroCoreApiUtils } from "@stellar/core-api-client";
import { withEllipsis } from "@styles/common-styles";
import { formatUserRoleType } from "@utils/data-display";
import { MEMBERS_TABLE_COLUMN_LABELS } from "@components/table/members/members-table-constants";
import { isMembersTableColumnField } from "@custom-types/type-guards";
import { useMediaQueryList } from "@hooks/use-media-query";
import { MemberOptionsMenu } from "@components/table/members/member-options-menu";
import { UiUtils } from "@stellar/utils";
import { useMemberSeatsLookup } from "@hooks/use-member-seats-lookup";
import { getHeldSeats, getSeatsRank } from "@utils/member-seats-utils";
/** Minimum column width in px */
const COLS_MIN_WIDTH_IN_PX = 150;

/**
 * Columns width in xs and sm screen
 */
const xsAndSmColumnsWidthPX = 200;

/**
 * like name, email, status and joined date in medium screens
 */
const mdColumnsWidthInPX = 250;

/** Return all the possible columns for members table */
export function useMembersColumns(t0) {
  const $ = _c(36);
  const {
    companyId,
    project,
    tableSubject,
    group,
    team
  } = t0;
  const {
    columnPreferences,
    visibleOrderedColumns
  } = useColumnPreferencesContext();
  const seatsLookup = useMemberSeatsLookup();
  const {
    isScreenMd,
    isScreenLgOrLarger
  } = useMediaQueryList();
  const {
    formatDate
  } = useDateTime();
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = _temp;
    t2 = _temp2;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  const t3 = tableSubject !== "team";
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = _temp3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== columnPreferences || $[4] !== companyId || $[5] !== formatDate || $[6] !== group || $[7] !== isScreenLgOrLarger || $[8] !== isScreenMd || $[9] !== project || $[10] !== seatsLookup || $[11] !== t3 || $[12] !== tableSubject || $[13] !== team || $[14] !== visibleOrderedColumns) {
    let t6;
    if ($[16] !== companyId || $[17] !== project || $[18] !== tableSubject) {
      t6 = params_2 => FaroCoreApiUtils.Member.isTeam(params_2.row.entity) ? <var>{UiUtils.DASH}</var> : <StatusColumn companyId={companyId} project={project} subjectType={tableSubject} member={params_2.row.entity} />;
      $[16] = companyId;
      $[17] = project;
      $[18] = tableSubject;
      $[19] = t6;
    } else {
      t6 = $[19];
    }
    let t7;
    if ($[20] !== companyId || $[21] !== group || $[22] !== project || $[23] !== tableSubject) {
      t7 = params_3 => <RoleColumn companyId={companyId} selectedProject={project} subjectType={tableSubject} memberIdentityRole={{
        identity: params_3.row.entity.identity,
        role: params_3.row.entity.role
      }} group={group} member={params_3.row.entity} />;
      $[20] = companyId;
      $[21] = group;
      $[22] = project;
      $[23] = tableSubject;
      $[24] = t7;
    } else {
      t7 = $[24];
    }
    let t8;
    if ($[25] !== formatDate) {
      t8 = params_4 => {
        if (isGroupMember(params_4.row.entity)) {
          return <Box component="var" sx={withEllipsis}>{formatDate(params_4.row.entity.dateJoined)}</Box>;
        }
      };
      $[25] = formatDate;
      $[26] = t8;
    } else {
      t8 = $[26];
    }
    let t9;
    if ($[27] !== seatsLookup) {
      t9 = (__5, row_5) => getSeatsRank(getHeldSeats(row_5.entity, seatsLookup));
      $[27] = seatsLookup;
      $[28] = t9;
    } else {
      t9 = $[28];
    }
    let t10;
    if ($[29] !== columnPreferences) {
      t10 = () => <FaroTableColumnVisibilityMenuIcon columnPreferences={columnPreferences} />;
      $[29] = columnPreferences;
      $[30] = t10;
    } else {
      t10 = $[30];
    }
    let t11;
    if ($[31] !== group || $[32] !== project || $[33] !== tableSubject || $[34] !== team) {
      t11 = params_6 => <MemberOptionsMenu selectedProject={project} subjectType={tableSubject} member={params_6.row.entity} group={group} team={team} />;
      $[31] = group;
      $[32] = project;
      $[33] = tableSubject;
      $[34] = team;
      $[35] = t11;
    } else {
      t11 = $[35];
    }
    const columnsMap = {
      name: {
        field: "name",
        headerName: MEMBERS_TABLE_COLUMN_LABELS.name,
        flex: 1,
        minWidth: COLS_MIN_WIDTH_IN_PX,
        disableReorder: true,
        renderCell: t1,
        valueGetter: t2,
        sortable: t3,
        sortComparator: t4
      },
      user: {
        field: "user",
        headerName: MEMBERS_TABLE_COLUMN_LABELS.user,
        flex: 1,
        minWidth: COLS_MIN_WIDTH_IN_PX,
        renderCell: _temp4,
        valueGetter: _temp5,
        sortable: tableSubject !== "team",
        disableReorder: true,
        sortComparator: _temp6
      },
      email: {
        field: "email",
        flex: isScreenLgOrLarger ? 1 : undefined,
        minWidth: COLS_MIN_WIDTH_IN_PX,
        width: isScreenMd ? mdColumnsWidthInPX : xsAndSmColumnsWidthPX,
        renderHeader: _temp7,
        renderCell: _temp8,
        valueGetter: _temp9,
        sortable: tableSubject !== "team",
        sortComparator: _temp0
      },
      status: {
        field: "status",
        flex: isScreenLgOrLarger ? 1 : undefined,
        minWidth: COLS_MIN_WIDTH_IN_PX,
        width: isScreenMd ? mdColumnsWidthInPX : xsAndSmColumnsWidthPX,
        renderHeader: _temp1,
        renderCell: t6,
        valueGetter: _temp10,
        sortable: tableSubject !== "team",
        sortComparator: _temp11
      },
      role: {
        field: "role",
        flex: isScreenLgOrLarger ? 1 : undefined,
        minWidth: COLS_MIN_WIDTH_IN_PX,
        maxWidth: isScreenMd ? mdColumnsWidthInPX : xsAndSmColumnsWidthPX,
        renderHeader: _temp12,
        renderCell: t7,
        valueGetter: _temp13,
        sortable: tableSubject !== "team",
        sortComparator: _temp14
      },
      joinedDate: {
        field: "joinedDate",
        flex: isScreenLgOrLarger ? 1 : undefined,
        minWidth: COLS_MIN_WIDTH_IN_PX,
        width: isScreenMd ? mdColumnsWidthInPX : xsAndSmColumnsWidthPX,
        renderHeader: _temp15,
        renderCell: t8,
        valueGetter: _temp16,
        sortable: true,
        sortComparator: _temp17
      },
      seats: {
        field: "seats",
        flex: isScreenLgOrLarger ? 1 : undefined,
        minWidth: COLS_MIN_WIDTH_IN_PX,
        width: isScreenMd ? mdColumnsWidthInPX : xsAndSmColumnsWidthPX,
        renderHeader: _temp18,
        renderCell: _temp19,
        valueGetter: t9,
        sortable: true,
        sortComparator: _temp20
      },
      options: {
        field: "options",
        type: "actions",
        align: "right",
        width: 90,
        disableReorder: true,
        renderHeader: t10,
        renderCell: t11
      }
    };
    t5 = visibleOrderedColumns.filter(isMembersTableColumnField).map(field => columnsMap[field]);
    $[3] = columnPreferences;
    $[4] = companyId;
    $[5] = formatDate;
    $[6] = group;
    $[7] = isScreenLgOrLarger;
    $[8] = isScreenMd;
    $[9] = project;
    $[10] = seatsLookup;
    $[11] = t3;
    $[12] = tableSubject;
    $[13] = team;
    $[14] = visibleOrderedColumns;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
}
function _temp20(rank1, rank2) {
  return rank2 - rank1;
}
function _temp19(params_5) {
  return <SeatsColumn member={params_5.row.entity} />;
}
function _temp18() {
  return <TableHeaderWithDraggableIcon title={MEMBERS_TABLE_COLUMN_LABELS.seats} />;
}
function _temp17(v1_4, v2_4, cp1_4, cp2_4) {
  return faroTableComparator(v1_4, v2_4, cp1_4, cp2_4, gridStringOrNumberComparator);
}
function _temp16(__4, row_4) {
  return isGroupMember(row_4.entity) ? row_4.entity.dateJoined : undefined;
}
function _temp15() {
  return <TableHeaderWithDraggableIcon title={MEMBERS_TABLE_COLUMN_LABELS.joinedDate} />;
}
function _temp14(v1_3, v2_3, cp1_3, cp2_3) {
  return faroTableComparator(v1_3, v2_3, cp1_3, cp2_3, gridStringOrNumberComparator);
}
function _temp13(__3, row_3) {
  return formatUserRoleType(row_3.entity.role);
}
function _temp12() {
  return <TableHeaderWithDraggableIcon title={MEMBERS_TABLE_COLUMN_LABELS.role} />;
}
function _temp11(v1_2, v2_2, cp1_2, cp2_2) {
  const status1 = FaroCoreApiUtils.Member.getMemberStatus(v1_2);
  const status2 = FaroCoreApiUtils.Member.getMemberStatus(v2_2);
  const teamComparison_0 = Number(FaroCoreApiUtils.Member.isTeam(v1_2)) - Number(FaroCoreApiUtils.Member.isTeam(v2_2));
  if (teamComparison_0 !== 0) {
    return teamComparison_0;
  }
  return gridStringOrNumberComparator(status1, status2, cp1_2, cp2_2);
}
function _temp10(__2, row_2) {
  return row_2.entity;
}
function _temp1() {
  return <TableHeaderWithDraggableIcon title={MemberHeaders.status} />;
}
function _temp0(v1_1, v2_1, cp1_1, cp2_1) {
  const teamComparison = Number(FaroCoreApiUtils.Member.isTeam(v1_1)) - Number(FaroCoreApiUtils.Member.isTeam(v2_1));
  if (teamComparison !== 0) {
    return teamComparison;
  }
  return gridStringOrNumberComparator(v1_1.email, v2_1.email, cp1_1, cp2_1);
}
function _temp9(__1, row_1) {
  return row_1.entity;
}
function _temp8(params_1) {
  return <BasicInfoMemberColumn member={params_1.row.entity} columnType={MemberHeaders.email} />;
}
function _temp7() {
  return <TableHeaderWithDraggableIcon title={MEMBERS_TABLE_COLUMN_LABELS.email} />;
}
function _temp6(v1_0, v2_0, cp1_0, cp2_0) {
  return faroTableComparator(v1_0, v2_0, cp1_0, cp2_0, gridStringOrNumberComparator);
}
function _temp5(__0, row_0) {
  return row_0.entity.name;
}
function _temp4(params_0) {
  return <Stack direction="row" sx={{
    ...withEllipsis,
    alignItems: "center",
    gap: "0.625rem"
  }}><UserAvatarWithMenu member={params_0.row.entity} /><BasicInfoMemberColumn member={params_0.row.entity} columnType={MemberHeaders.user} /></Stack>;
}
function _temp3(v1, v2, cp1, cp2) {
  return faroTableComparator(v1, v2, cp1, cp2, gridStringOrNumberComparator);
}
function _temp2(_, row) {
  return row.entity.name;
}
function _temp(params) {
  return <Stack direction="row" sx={{
    ...withEllipsis,
    alignItems: "center",
    gap: "0.625rem"
  }}><UserAvatarWithMenu member={params.row.entity} /><BasicInfoMemberColumn member={params.row.entity} columnType={MemberHeaders.name} /></Stack>;
}